import React, { useState } from "react";
import DashboardShell from "./components/DashboardShell";
import { primaryDebtor as getPrimaryDebtor } from "../utils/debtors";

const API_URL = process.env.REACT_APP_API_URL;

function formatDateTime(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return value;
  }
}

function formatDateOnly(value) {
  if (!value) return "—";
  try {
    // Prevent YYYY-MM-DD strings from being parsed as UTC and shifting a day
    if (typeof value === "string") {
      const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (match) {
        const [, year, month, day] = match;
        const d = new Date(Number(year), Number(month) - 1, Number(day));
        return d.toLocaleDateString("en-US", {
          month: "long",
          day: "numeric",
          year: "numeric",
        });
      }
    }

    const d = new Date(value);
    return d.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return value;
  }
}

export default function CaseDateReportPage() {
  const [reportType, setReportType] = useState("341");
  const [date, setDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [report, setReport] = useState(null);

  const fetchReport = async () => {
    if (!date) {
      setError("Please choose a date.");
      return;
    }

    setLoading(true);
    setError("");

    const token = localStorage.getItem("accessToken");

    try {
      const res = await fetch(
        `${API_URL}/reports/case-date/?report_type=${encodeURIComponent(reportType)}&date=${encodeURIComponent(date)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.detail || "Failed to load report.");
      }

      setReport(data);
    } catch (err) {
      setError(err.message || "Failed to load report.");
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <DashboardShell>
    <div className="max-w-7xl mx-auto px-4 py-6">
      <style>{`
        @media print {
          .no-print {
            display: none !important;
          }
          body {
            background: white !important;
          }
          .print-card {
            break-inside: avoid;
            page-break-inside: avoid;
          }
        }
      `}</style>

      <div className="no-print bg-white border border-gray-200 rounded-xl shadow-sm p-4 mb-6">
        <h1 className="text-2xl font-semibold text-gray-900 mb-4">341 / Hearing Report</h1>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <select
            value={reportType}
            onChange={(e) => setReportType(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2"
          >
            <option value="341">341 Report</option>
            <option value="hearing">Hearing Report</option>
          </select>

          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2"
          />

          <button
            type="button"
            onClick={fetchReport}
            className="bg-indigo-600 text-white rounded-lg px-4 py-2 hover:bg-indigo-700"
          >
            {loading ? "Loading..." : "Generate Report"}
          </button>

          <button
            type="button"
            onClick={handlePrint}
            className="border border-gray-300 bg-white text-gray-700 rounded-lg px-4 py-2 hover:bg-gray-50"
            disabled={!report}
          >
            Print / Save PDF
          </button>
        </div>

        {error ? <div className="text-red-600 text-sm mt-3">{error}</div> : null}
      </div>

      {report ? (
        <div>
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5 mb-6">
            <h2 className="text-xl font-semibold text-gray-900">
              {report.report_type === "341" ? "341 Report" : "Hearing Report"} — {formatDateOnly(report.date)}
            </h2>
            <div className="text-sm text-gray-600 mt-1">
              Total cases: {report.count}
            </div>
          </div>

          <div className="space-y-6">
            {report.cases.length === 0 ? (
              <div className="bg-white border border-gray-200 rounded-xl p-6 text-gray-600">
                No matching cases found.
              </div>
            ) : (
              report.cases.map((c) => {
                const primaryDebtor = getPrimaryDebtor(c.debtors, { filingOnly: true });

                return (
                  <div
                    key={c.case_id}
                    className="print-card bg-white border border-gray-200 rounded-xl shadow-sm p-5"
                  >
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {primaryDebtor
                            ? `${primaryDebtor.first_name} ${primaryDebtor.second_name ? `${primaryDebtor.second_name} ` : ""}${primaryDebtor.last_name}`
                            : "Unnamed Client"}
                        </h3>

                        <div className="mt-2 text-sm text-gray-700 space-y-1">
                          <div><span className="font-medium">Case ID:</span> {c.case_id}</div>
                          <div><span className="font-medium">Case Number:</span> {c.case_number || "TBD"}</div>
                          <div><span className="font-medium">Chapter:</span> {c.chapter}</div>
                          <div><span className="font-medium">Status:</span> {c.status}</div>
                          <div><span className="font-medium">Email:</span> {primaryDebtor?.email || "N/A"}</div>
                          <div><span className="font-medium">Phone:</span> {primaryDebtor?.phone || "N/A"}</div>
                          <div>
                            <span className="font-medium">341 Date:</span> {formatDateTime(c.meeting_date)}
                          </div>
                          <div>
                            <span className="font-medium">Hearing Date:</span> {formatDateOnly(c.confirmation_date)}
                          </div>
                        </div>
                      </div>

                      <div>
                        <div className="mb-4">
                          <h4 className="text-sm font-semibold text-indigo-700 mb-2">341 Tasks</h4>
                          {c.tasks_341?.length ? (
                            <div className="space-y-2">
                              {c.tasks_341.map((t) => (
                                <div key={t.id} className="border border-gray-200 rounded-lg p-3">
                                  <div className="flex items-center justify-between gap-3">
                                    <div className="font-medium text-gray-900">{t.title}</div>
                                    <div className="text-xs text-gray-500">{t.status}</div>
                                  </div>
                                  {t.description ? (
                                    <div className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">
                                      {t.description}
                                    </div>
                                  ) : null}
                                  <div className="text-xs text-gray-500 mt-2">
                                    Due: {t.due_date || "—"}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-sm text-gray-500">No 341 tasks.</div>
                          )}
                        </div>

                        <div>
                          <h4 className="text-sm font-semibold text-indigo-700 mb-2">341 Notes</h4>
                          {c.notes_341?.length ? (
                            <div className="space-y-2">
                              {c.notes_341.map((n) => (
                                <div key={n.id} className="border border-gray-200 rounded-lg p-3">
                                  <div className="text-xs text-gray-500 mb-1">
                                    {formatDateTime(n.created_at)}
                                  </div>
                                  <div className="text-sm text-gray-800 whitespace-pre-wrap">
                                    {n.note}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-sm text-gray-500">No 341 notes.</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      ) : null}
    </div>
    </DashboardShell>
  );
}
